import { claimAccessAPI } from '@apiRoutes'
import type { ClaimResult } from '@apiRoutes'
import {
  ClaimErrorCode,
  ClaimErrorDetails,
  getClaimErrorCode,
  getClaimErrorCodeFromException
} from './claim-errors'

export type ClaimDoneState = {
  claim: ClaimResult
  practiceName?: string | null
}

type ClaimOutcome =
  | { ok: true; claim: ClaimResult }
  | { ok: false; code: ClaimErrorCode; details?: ClaimErrorDetails }

/*
  POST /claim is idempotent, so calling it again with the same account returns
  the same 200. Only the claim page calls it: for an unused link, after the
  patient confirms the signed-in account.
*/
export const performClaim = async (
  getAccessTokenSilently: () => Promise<string>,
  token: string
): Promise<ClaimOutcome> => {
  try {
    const authToken = await getAccessTokenSilently()
    const response = await claimAccessAPI(authToken, token)

    if (response.success) {
      return { ok: true, claim: response.responsePayload }
    }

    const payload = response.responsePayload
    return {
      ok: false,
      code: getClaimErrorCode(response.status, payload?.code),
      details: {
        activeUntil: payload?.activeUntil,
        linkExpiresAt: payload?.linkExpiresAt
      }
    }
  } catch (err) {
    const e = err as { error?: string; message?: string }
    // Auth0 SDK errors (login_required, consent_required, ...) mean the session is gone.
    if (e && typeof e.error === 'string') {
      return { ok: false, code: 'unauthorized' }
    }
    return { ok: false, code: getClaimErrorCodeFromException(err) }
  }
}
